import { useEffect, useState } from 'react';
import { Button, Field, Header, Icon, IconName, Input, Message, Modal, Table, Dropdown, useFrontier, Pagination } from 'react-frontier-v2';
import { IssueType, SetLoading } from '../SiteClasses';
import { useBreadcrumbs, useUser } from '../hooks';
import UserAccess from '../UserAccess';
import { ISSUE_ICON_NAMES } from '../iconNames';
import Validator from '../Validator';
import API from '../API';

var PAGE_SIZE = 20;

interface IssueTypeForm {
   type_name: string;
   type_icon: string;
}

var IssueTypesList = ()=>{
   var { setTitle } = useBreadcrumbs();
   var { hasAccess } = useUser();
   var { Toast } = useFrontier();
   var [types, setTypes] = useState<IssueType[]>(null);
   var [loadError, setLoadError] = useState<string>(null);
   var [search, setSearch] = useState<string>('');
   var [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout>>(null);
   var [page, setPage] = useState<number>(0);

   var [showCreate, setShowCreate] = useState<boolean>(false);
   var [showEdit, setShowEdit] = useState<boolean>(false);
   var [showDelete, setShowDelete] = useState<boolean>(false);
   var [selectedType, setSelectedType] = useState<IssueType>(null);
   var [form, setForm] = useState<IssueTypeForm>({ type_name: '', type_icon: 'ticket' });
   var [modalPrompts, setModalPrompts] = useState<string[]>(null);

   useEffect(()=>{
      setTitle('Tipos de tickets');
      fetchTypes(0, '');
   }, []);

   useEffect(()=>{
      return ()=>{
         if(searchTimeout) clearTimeout(searchTimeout);
      };
   }, [searchTimeout]);

   var fetchTypes = (get_page?: number, get_search?: string)=>{
      var p = get_page !== undefined ? get_page : page;
      var q = get_search !== undefined ? get_search : search;
      var offset = p * PAGE_SIZE;
      setLoadError(null);
      setTypes(null);
      API.getAllIssueType(q || null, offset, PAGE_SIZE).then(res=>{
         if(res.error){
            setLoadError(res.message || 'Error al cargar los tipos de tickets');
            setTypes([]);
            return;
         }
         setTypes(res.data || []);
      }).catch(()=>{
         setLoadError('Hubo un error al obtener los tipos de tickets');
         setTypes([]);
      });
   };

   var onSearchChange = (val: string)=>{
      setSearch(val);
      if(searchTimeout) clearTimeout(searchTimeout);
      if(!val.trim()){
         setPage(0);
         fetchTypes(0, '');
         return;
      }
      setSearchTimeout(setTimeout(()=>{
         setPage(0);
         fetchTypes(0, val);
      }, 400));
   };

   var onPageChange = (newPage: number)=>{
      setPage(newPage);
      fetchTypes(newPage, search);
   };

   var openCreate = ()=>{
      setForm({ type_name: '', type_icon: 'ticket' });
      setModalPrompts(null);
      setShowCreate(true);
   };

   var openEdit = (type: IssueType)=>{
      setSelectedType(type);
      setForm({
         type_name: type.type_name,
         type_icon: type.type_icon || 'ticket'
      });
      setModalPrompts(null);
      setShowEdit(true);
   };

   var openDelete = (type: IssueType)=>{
      setSelectedType(type);
      setModalPrompts(null);
      setShowDelete(true);
   };

   var submitCreate = async (setBtnLoading: SetLoading)=>{
      var { valid, prompts } = Validator(form, {
         type_name: [{ rule: 'empty', label: 'Nombre', prompt: 'Favor de ingresar el nombre del tipo' }],
         type_icon: [{ rule: 'empty', label: 'Ícono', prompt: 'Favor de seleccionar un ícono' }]
      });

      setModalPrompts(prompts);
      if(!valid) return;

      setBtnLoading(true);
      API.createIssueType({
         type_name: form.type_name.trim(),
         type_icon: form.type_icon || 'ticket'
      }).then(res=>{
         if(res.error){
            setModalPrompts([res.message || 'Error al crear el tipo']);
            return;
         }
         var newType: IssueType = {
            type_id: res.data.type_id,
            type_name: form.type_name.trim(),
            type_icon: form.type_icon || 'ticket'
         };
         setTypes(prev=>[newType, ...(prev || [])]);
         Toast.success('Tipo de ticket creado exitosamente');
         setShowCreate(false);
      }).catch(()=>{
         setModalPrompts(['Error inesperado al crear el tipo de ticket']);
      }).finally(()=>{
         setBtnLoading(false);
      });
   };

   var submitEdit = async (setBtnLoading: SetLoading)=>{
      if(!selectedType) return;

      var { valid, prompts } = Validator(form, {
         type_name: [{ rule: 'empty', label: 'Nombre', prompt: 'Favor de ingresar el nombre del tipo' }],
         type_icon: [{ rule: 'empty', label: 'Ícono', prompt: 'Favor de seleccionar un ícono' }]
      });

      setModalPrompts(prompts);
      if(!valid) return;

      setBtnLoading(true);
      API.editIssueType({
         type_id: selectedType.type_id,
         type_name: form.type_name.trim(),
         type_icon: form.type_icon || 'ticket'
      }).then(res=>{
         if(res.error){
            setModalPrompts([res.message || 'Error al actualizar el tipo']);
            return;
         }
         setTypes(prev=>(prev || []).map(t=>t.type_id === selectedType.type_id ? {
            ...t,
            type_name: form.type_name.trim(),
            type_icon: form.type_icon || 'ticket'
         } : t));
         Toast.success('Tipo de ticket actualizado exitosamente');
         setShowEdit(false);
      }).catch(()=>{
         setModalPrompts(['Error inesperado al actualizar el tipo de ticket']);
      }).finally(()=>{
         setBtnLoading(false);
      });
   };

   var submitDelete = async (setBtnLoading: SetLoading)=>{
      if(!selectedType) return;

      setBtnLoading(true);
      API.deleteIssueType(selectedType.type_id).then(res=>{
         if(res.error){
            setModalPrompts([res.message || 'Error al eliminar el tipo']);
            return;
         }
         setTypes(prev=>(prev || []).filter(t=>t.type_id !== selectedType.type_id));
         Toast.success('Tipo de ticket eliminado exitosamente');
         setShowDelete(false);
      }).catch(()=>{
         setModalPrompts(['Error inesperado al eliminar el tipo de ticket']);
      }).finally(()=>{
         setBtnLoading(false);
      });
   };

   var iconDropdownItems = ISSUE_ICON_NAMES.map(i=>({
      value: i.value,
      text: (
         <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <Icon name={i.value} />
            <span>{i.text}</span>
         </div>
      )
   }));

   return <div>
      <Table 
         striped 
         style={{ width: '500px', margin: '20px auto' }}
      >
         <Table.Head 
            title="Tipos de tareas"
            titleRight={hasAccess(UserAccess.ISSUES.CREATE_ISSUE_TYPES) ? (
               <Button
                  iconName="plus"
                  color="black"
                  text="Agregar tipo"
                  size="small"
                  onClick={openCreate}
               />
            ) : undefined}
         >
            <Table.Row>
               <Table.Cell colSpan={4}>
                  <div style={{ display: 'flex', gap: 10, padding: 10 }}>
                     <Input
                        placeholder="Buscar tipo de ticket..."
                        value={search}
                        onChange={onSearchChange}
                        style={{ width: '100%' }}
                     />
                  </div>
               </Table.Cell>
            </Table.Row>
            <Table.Row>
               <Table.Cell header>ID</Table.Cell>
               <Table.Cell header centered>Ícono</Table.Cell>
               <Table.Cell header>Nombre</Table.Cell>
               <Table.Cell centered header><Icon name="wrench" /></Table.Cell>
            </Table.Row>
         </Table.Head>
         <Table.Body>
            {!types ? (
               <Table.Row>
                  <Table.Cell colSpan={4} centered>
                     <Header loading text="Cargando datos..." />
                  </Table.Cell>
               </Table.Row>
            ) : loadError ? (
               <Table.Row empty>
                  <Table.Cell colSpan={4}>{loadError}</Table.Cell>
               </Table.Row>
            ) : types.length > 0 ? (
               types.map(type=>(
                  <Table.Row
                     key={type.type_id}
                     collapsingIndexes={[0, 1, 3]}
                     centeredIndexes={[1, 3]}
                     data={[
                        type.type_id,
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                           <Icon name={(type.type_icon as IconName) || 'ticket'} style={{ fontSize: 16 }} />
                        </div>,
                        <span style={{ fontWeight: 500 }}>{type.type_name}</span>,
                        (hasAccess(UserAccess.ISSUES.EDIT_ISSUE_TYPES) || hasAccess(UserAccess.ISSUES.DELETE_ISSUE_TYPES)) ? (
                           <Button
                              icon
                              iconName="ellipsis"
                              size="small"
                              menu={[
                                 ...(hasAccess(UserAccess.ISSUES.EDIT_ISSUE_TYPES) ? [{
                                    text: 'Editar',
                                    iconName: 'pencil' as IconName,
                                    onClick: ()=>openEdit(type)
                                 }] : []),
                                 ...(hasAccess(UserAccess.ISSUES.DELETE_ISSUE_TYPES) ? [{
                                    text: 'Eliminar',
                                    iconName: 'trash' as IconName,
                                    onClick: ()=>openDelete(type)
                                 }] : [])
                              ]}
                           />
                        ) : null
                     ]}
                  />
               ))
            ) : (
               <Table.Row empty>
                  <Table.Cell colSpan={4}>No se encontraron tipos de tickets</Table.Cell>
               </Table.Row>
            )}
         </Table.Body>
         <Table.Footer actions={(
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
               <Pagination
                  page={page}
                  disabled={!types}
                  onPageChange={onPageChange}
                  hasNext={types ? types.length >= PAGE_SIZE : false}
               />
            </div>
         )} />
      </Table>

         <Modal open={showCreate} onClose={()=>setShowCreate(false)} size="tiny">
            <Modal.Header>Agregar tipo de tarea</Modal.Header>
            <Modal.Content>
               <Field label="Nombre">
                  <Input
                     value={form.type_name}
                     onChange={(val)=>setForm(prev=>({ ...prev, type_name: val }))}
                     placeholder="Ej: Solicitud de equipo"
                  />
               </Field>
               <Dropdown
                  label='Ícono'
                  value={form.type_icon}
                  items={iconDropdownItems}
                  onValueChange={(val: string)=>{
                     setForm(prev=>({ ...prev, type_icon: val }));
                  }}
                  style={{ width: '100%' }}
               />

               {modalPrompts && modalPrompts.length > 0 && (
                  <Message style={{ marginTop: 15 }} type="error" list={modalPrompts} />
               )}
            </Modal.Content>
            <Modal.Actions>
               <Button text="Cancelar" basic onClick={()=>setShowCreate(false)} />
               <Button text="Guardar" color="black" onClick={submitCreate} />
            </Modal.Actions>
         </Modal>

         <Modal open={showEdit} onClose={()=>setShowEdit(false)} size="tiny">
            <Modal.Header>Editar tipo de ticket</Modal.Header>
            <Modal.Content>
               <Field label="Nombre">
                  <Input
                     value={form.type_name}
                     onChange={(val)=>setForm(prev=>({ ...prev, type_name: val }))}
                     placeholder="Ej: Solicitud de equipo"
                  />
               </Field>

               <Field label="Ícono" style={{ marginTop: 15 }}>
                  <Dropdown
                     value={form.type_icon}
                     items={iconDropdownItems}
                     onValueChange={(val: string)=>{
                        setForm(prev=>({ ...prev, type_icon: val }));
                     }}
                     style={{ width: '100%' }}
                  />
               </Field>

               {modalPrompts && modalPrompts.length > 0 && (
                  <Message style={{ marginTop: 15 }} type="error" list={modalPrompts} />
               )}
            </Modal.Content>
            <Modal.Actions>
               <Button text="Cancelar" basic onClick={()=>setShowEdit(false)} />
               <Button text="Guardar" color="black" onClick={submitEdit} />
            </Modal.Actions>
         </Modal>

         <Modal open={showDelete} onClose={()=>setShowDelete(false)} size="tiny">
            <Modal.Header>Eliminar tipo de ticket</Modal.Header>
            <Modal.Content>
               <Header
                  text="¿Estás seguro de que deseas eliminar este tipo de ticket?"
                  subtext="El tipo será marcado como eliminado y no estará disponible para nuevos tickets."
               />
               {selectedType && (
                  <Table details style={{ marginTop: 20 }}>
                     <Table.Body>
                        <Table.Row data={['ID', selectedType.type_id]} />
                        <Table.Row data={['Nombre', selectedType.type_name]} />
                        <Table.Row data={['Ícono', <Icon name={(selectedType.type_icon as IconName) || 'ticket'} />]} />
                     </Table.Body>
                  </Table>
               )}
               {modalPrompts && modalPrompts.length > 0 && (
                  <Message style={{ marginTop: 15 }} type="error" list={modalPrompts} />
               )}
            </Modal.Content>
            <Modal.Actions>
               <Button text="Cancelar" basic onClick={()=>setShowDelete(false)} />
               <Button text="Eliminar" color="red" onClick={submitDelete} />
            </Modal.Actions>
         </Modal>
      </div>
};

export default IssueTypesList;
